import React from 'react';
import { ConfigProvider, Skeleton, Table, TableColumnType } from 'antd';
import styled from 'styled-components';

import IconChevronDown from '@/assets/icon-chevron-down.svg?react';
import { getLanguage } from '@/utils/util-functions';

interface EPTableProps {
  expandable?: Record<string, unknown>;
  loading?: boolean;
  loadingRowCount?: number;
  columns?: TableColumnType<unknown>[];
  [key: string]: unknown;
}

const EPTable: React.FC<EPTableProps> = ({ expandable = {}, ...props }) => {
  const savedLanguage = localStorage.getItem('language');

  const { columns, loading, loadingRowCount } = props;

  return (
    <ConfigProvider locale={getLanguage(savedLanguage)}>
      {loading ? (
        <StyledTable
          rowKey="key"
          pagination={false}
          dataSource={[...Array(loadingRowCount)].map((_, index) => ({
            key: `key${index}`,
          }))}
          columns={columns.map(column => ({
            ...column,
            render: () => <Skeleton key={column.key} active title={{ width: '100%' }} paragraph={false} />,
          }))}
        />
      ) : (
        <StyledTable
          {...props}
          expandable={{
            expandIcon: ({
              expanded,
              onExpand,
              record,
            }: {
              expanded: boolean;
              onExpand: (record: unknown, e: React.MouseEvent<HTMLElement>) => void;
              record: { children?: unknown };
            }) => {
              if (!record.children) return null;

              return expanded ? (
                <IconChevronDown
                  style={{ cursor: 'pointer' }}
                  onClick={e => onExpand(record, e as unknown as React.MouseEvent<HTMLElement>)}
                />
              ) : (
                <IconChevronDown
                  style={{
                    cursor: 'pointer',
                    transform: 'rotate(-90deg)',
                  }}
                  onClick={e => onExpand(record, e as unknown as React.MouseEvent<HTMLElement>)}
                />
              );
            },
            ...expandable,
          }}
        />
      )}
    </ConfigProvider>
  );
};

const StyledTable = styled(Table)`
  .ant-table {
    color: ${({ theme: { token } }) => token.colorText};

    .ant-table-cell::before {
      display: none;
    }
    .ant-table-header {
      border-radius: 0;
    }

    .ant-table-container table > thead > tr:first-child > *:last-child {
      border-radius: 0;
    }

    .ant-table-container table > thead > tr:first-child > *:first-child {
      border-radius: 0;
    }

    .ant-table-thead > tr > th {
      border-bottom: 2px solid ${({ theme: { token } }) => token.colorBorder};
      color: ${({ theme: { token } }) => token.colorText};
      position: sticky;
      top: 0;
      z-index: 1;
    }

    .ant-table-tbody > tr.ant-table-row:hover > td {
      background: inherit;
    }

    td.ant-table-column-sort {
      background: inherit;
    }

    .ant-table-tbody > tr {
      > td {
        border-bottom: none;
        border-top: 1px solid ${({ theme: { token } }) => token.colorBorder};
      }

      &:first-child {
        > td {
          border-top: none;
        }
      }

      &:last-child {
        > td {
          border-bottom: 1px solid ${({ theme: { token } }) => token.colorBorder};
        }
      }

      &.ant-table-row-level-1 {
        > td {
          border-top: none;
        }
      }
    }
  }

  .ant-table-empty .ant-table-tbody > tr.ant-table-placeholder {
    color: ${({ theme: { token } }) => token.colorText};

    &:hover {
      > td {
        background-color: inherit;
      }
    }
  }
`;

export default EPTable;
